// client/src/pages/admin/AdminDashboard.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { 
  FaUsers, 
  FaShoppingCart, 
  FaMoneyBillWave, 
  FaEye,
  FaClock,
  FaProductHunt
} from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_BASE_URL || "http://localhost:5000/";

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalCategories: 0,
    featuredProducts: 0,
  });
  const [analytics, setAnalytics] = useState({
    totalUsers: 0,
    totalOrders: 0,
    totalRevenue: 0,
    recentSessions: 0,
    recentOrders: 0,
    avgSessionDuration: 0
  });
  const [randomProducts, setRandomProducts] = useState([]);
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);

        // 1) Fetch all products, categories, and analytics
        const [productsRes, categoriesRes, analyticsRes] = await Promise.all([
          api.get('/products'),
          api.get('/categories'),
          api.get('/analytics/dashboard').catch(() => ({ data: { overview: {}, sessionAnalytics: {} } }))
        ]);
        const products = productsRes.data;
        const categories = categoriesRes.data;
        const analyticsData = analyticsRes.data;

        // 2) Compute “stat cards”
        const featuredCount = products.filter((p) => p.featured).length;
        setStats({
          totalProducts: products.length,
          totalCategories: categories.length,
          featuredProducts: featuredCount,
        });

        // 3) Set analytics data
        setAnalytics({
          totalUsers: analyticsData.overview?.totalUsers || 0,
          totalOrders: analyticsData.overview?.totalOrders || 0,
          totalRevenue: analyticsData.overview?.totalRevenue || 0,
          recentSessions: analyticsData.overview?.recentSessions || 0,
          recentOrders: analyticsData.overview?.recentOrders || 0,
          avgSessionDuration: analyticsData.sessionAnalytics?.avgSessionDuration || 0
        });

        // 4) Pick 10 random products
        const shuffled = [...products].sort(() => 0.5 - Math.random());
        const tenRandom = shuffled.slice(0, 10);
        setRandomProducts(tenRandom);

        // 4) Build “Products by Category” chart data
        const labels = categories.map((c) => c.name);
        const counts = categories.map((cat) =>
          products.filter(
            (p) =>
              Array.isArray(p.categories) &&
              p.categories.some((c) => c.id === cat.id)
          ).length
        );
        setChartData({
          labels,
          datasets: [
            {
              label: 'Products per Category',
              data: counts,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              borderColor: 'rgba(0, 0, 0, 1)',
              borderWidth: 1,
            },
          ],
        });
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Helper function to format duration
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // Chart configuration
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: 'Products by Category' },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { precision: 0 }, // integer tick labels
      },
    },
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Dashboard</h1>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
        </div>
      ) : (
        <>
          {/* ─── Analytics Cards ───────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <AnalyticsCard 
              title="Total Users" 
              value={analytics.totalUsers} 
              icon={<FaUsers className="text-gray-700 text-xl" />}
              bgColor="bg-gray-100"
            />
            <AnalyticsCard 
              title="Total Orders" 
              value={analytics.totalOrders} 
              icon={<FaShoppingCart className="text-gray-700 text-xl" />}
              bgColor="bg-gray-100"
            />
            <AnalyticsCard 
              title="Total Revenue" 
              value={`₹${analytics.totalRevenue.toLocaleString()}`} 
              icon={<FaMoneyBillWave className="text-gray-700 text-xl" />}
              bgColor="bg-gray-100"
            />
            <AnalyticsCard 
              title="Avg Session Time" 
              value={formatDuration(analytics.avgSessionDuration)} 
              icon={<FaClock className="text-gray-700 text-xl" />}
              bgColor="bg-gray-100"
            />
          </div>

          {/* ─── Additional Analytics Cards ────────────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <AnalyticsCard 
              title="Recent Sessions" 
              value={analytics.recentSessions} 
              icon={<FaEye className="text-gray-700 text-xl" />}
              bgColor="bg-gray-100"
            />
            <AnalyticsCard 
              title="Recent Orders" 
              value={analytics.recentOrders} 
              icon={<FaProductHunt className="text-gray-700 text-xl" />}
              bgColor="bg-gray-100"
            />
          </div>

          {/* ─── Stats Cards ────────────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <StatCard title="Total Products" value={stats.totalProducts} icon="📦" />
            <StatCard title="Categories" value={stats.totalCategories} icon="🗂️" />
            <StatCard title="Featured Products" value={stats.featuredProducts} icon="⭐" />
          </div>

          {/* ─── Products by Category Chart ─────────────────────────────────────── */}
          <div className="bg-white p-6 rounded-lg shadow-md mb-8">
            <h2 className="text-xl font-semibold mb-4">Analytics</h2>
            <div className="h-80">
              <Bar options={chartOptions} data={chartData} />
            </div>
          </div>

          {/* ─── Random Products Table ─────────────────────────────────────────── */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Recent Products</h2>
              <Link to="/admin/products" className="text-black hover:underline">
                View All
              </Link>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['Product', 'Category', 'Status', 'Actions'].map((heading, idx) => (
                      <th
                        key={idx}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {randomProducts.length > 0 ? (
                    randomProducts.map((prod) => {
                      //
                      // Extract the “first image” robustly, covering:
                      //  • Array-of-strings (prod.images = ['uploads/a.jpg', ...])
                      //  • JSON-encoded string (prod.images = '["uploads/a.jpg","uploads/b.jpg"]')
                      //  • Comma-separated string (prod.images = 'uploads/a.jpg,uploads/b.jpg')
                      //
                      let rawImg = '';

                      if (Array.isArray(prod.images)) {
                        rawImg = prod.images[0] || '';
                      } else if (typeof prod.images === 'string') {
                        // Try JSON.parse first
                        try {
                          const parsed = JSON.parse(prod.images);
                          if (Array.isArray(parsed)) {
                            rawImg = parsed[0] || '';
                          } else {
                            rawImg = prod.images;
                          }
                        } catch {
                          // Fallback: split on comma
                          const parts = prod.images.split(',');
                          rawImg = parts[0].trim();
                        }
                      }

                      // Remove any leading slash(es):
                      const cleanedPath = rawImg.replace(/^\/+/, '');

                      // Build final thumbnail URL:
                      let thumbnailUrl;
                      if (!cleanedPath) {
                        thumbnailUrl = '/placeholder.png';
                      } else if (cleanedPath.startsWith('http')) {
                        thumbnailUrl = cleanedPath;
                      } else {
                        thumbnailUrl = `${API_BASE}cleanedPath`;
                      }

                      const categoryNames = Array.isArray(prod.categories)
                        ? prod.categories.map((c) => c.name).join(', ')
                        : 'Uncategorized';

                      return (
                        <tr key={prod.id} className="hover:bg-gray-50">
                          {/* Product + Thumbnail */}
                          <td className="px-6 py-4 whitespace-nowrap flex items-center">
                            <img
                              src={thumbnailUrl}
                              alt={prod.name}
                              className="h-10 w-10 rounded-full object-cover mr-4"
                              onError={(e) => {
                                // If image fails to load, fallback to placeholder
                                e.currentTarget.src = '/placeholder.png';
                              }}
                            />
                            <span className="font-medium">{prod.name}</span>
                          </td>

                          {/* Category Column */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {categoryNames}
                          </td>

                          {/* Status Column */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                prod.published
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {prod.published ? 'Published' : 'Draft'}
                            </span>
                          </td>

                          {/* Actions Column */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-4">
                            <Link
                              to={`/admin/products?edit=${prod.id}`}
                              className="text-black hover:text-gray-700"
                            >
                              Edit
                            </Link>
                            <Link
                              to={`/products/${prod.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-600 hover:text-gray-900"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                        No products found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Reusable AnalyticsCard Component
// ─────────────────────────────────────────────────────────────────────────────
function AnalyticsCard({ title, value, icon, bgColor }) {
  return (
    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 ${bgColor} rounded-full`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reusable StatCard Component
// ─────────────────────────────────────────────────────────────────────────────
function StatCard({ title, value, icon }) {
  return (
    <div className="bg-white p-6 rounded-lg shadow-md flex items-center">
      <div className="text-3xl mr-4">{icon}</div>
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-2xl font-bold">{value}</p>
      </div>
    </div>
  );
}

export default AdminDashboard;
